/**
 * Derivation of the task shown to users after a Kinnser scheduling push
 * (`schedule_in_kinnser`). The response identifies the clinical ROC/SOC task
 * (`patient_task_key` / `task_name`) and, when the agency configured an H&P
 * upload task, the task the referral documents actually landed on
 * (`upload_patient_task_key` / `upload_task_name`). The success panel and the
 * packaged create result must both name the upload task, so they share this
 * single derivation.
 */

export const getStringField = (
  value: unknown,
  fieldName: string
): string | null => {
  if (value == null || typeof value !== 'object' || !(fieldName in value)) {
    return null;
  }

  const fieldValue = (value as Record<string, unknown>)[fieldName];
  return typeof fieldValue === 'string' && fieldValue.trim() !== ''
    ? fieldValue
    : null;
};

export interface SchedulingDisplayTask {
  taskKey: string | null;
  taskName: string;
}

/**
 * Prefer the H&P upload task (when the agency configured one) so the UI names
 * the task the referral documents actually landed on; otherwise fall back to
 * the clinical ROC/SOC task, then to `fallbackTaskName`.
 */
export function deriveSchedulingDisplayTask(
  schedulingResult: unknown,
  fallbackTaskName: string
): SchedulingDisplayTask {
  return {
    taskKey:
      getStringField(schedulingResult, 'upload_patient_task_key') ??
      getStringField(schedulingResult, 'patient_task_key'),
    taskName:
      getStringField(schedulingResult, 'upload_task_name') ??
      getStringField(schedulingResult, 'task_name') ??
      fallbackTaskName,
  };
}
